import { useAppSelector } from 'app/hooks'
import { ScreenOrientation } from 'app/types'
import React, { useEffect, useRef, useCallback } from 'react'

interface IntersectionHelperProps {
    options?: Dict,
    attr?: string,
    scrollElem?: HTMLElement,
}

/**
 * Finds the nearest scrollable ancestor for use as IntersectionObserver root.
 * This is critical for cross-platform compatibility — Safari/Mac may fail to
 * detect intersections when the root is null (viewport) but content scrolls
 * inside a custom scroll container.
 */
function findScrollContainer(startElement?: HTMLElement): HTMLElement | null {
    // Try explicit scroll element first
    if (startElement) {
        return startElement
    }
    // Query the known scroll container pattern used in DynamicPage
    const scrollable = document.querySelector('.content.dynamic.scrollable')
    if (scrollable instanceof HTMLElement) {
        return scrollable
    }
    return null
}

/**
 * Check if an element is currently within the visible bounds of a scroll container
 * or the viewport. Used as a fallback when IntersectionObserver misses elements.
 */
function isElementVisible(el: Element, scrollContainer: HTMLElement | null): boolean {
    const rect = el.getBoundingClientRect()

    if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()
        // Element overlaps with the scroll container's visible area
        return (
            rect.bottom > containerRect.top &&
            rect.top < containerRect.bottom &&
            rect.right > containerRect.left &&
            rect.left < containerRect.right
        )
    }

    // Fallback to viewport check
    return (
        rect.bottom > 0 &&
        rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right > 0 &&
        rect.left < (window.innerWidth || document.documentElement.clientWidth)
    )
}

export function IntersectionHelper(props: IntersectionHelperProps) {
    const screen = useAppSelector((state: RootState) => state.app.screen)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const observedElements = useRef<Set<Element>>(new Set())
    const fallbackTimers = useRef<Map<Element, ReturnType<typeof setTimeout>>>(new Map())

    /**
     * Make an element visible — adds the "visible" class and cleans up tracking.
     */
    const makeVisible = useCallback((el: Element) => {
        if (!el.classList.contains('visible')) {
            el.classList.add('visible')
        }
        // Clear any pending fallback timer for this element
        const timer = fallbackTimers.current.get(el)
        if (timer) {
            clearTimeout(timer)
            fallbackTimers.current.delete(el)
        }
        // Unobserve — once visible, no need to keep watching
        if (observerRef.current) {
            observerRef.current.unobserve(el)
        }
        observedElements.current.delete(el)
    }, [])

    /**
     * Handle IntersectionObserver callback.
     */
    const inViewport = useCallback((entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                setTimeout(() => {
                    makeVisible(entry.target)
                }, 200)
            }
        })
    }, [makeVisible])

    useEffect(() => {
        const scrollContainer = findScrollContainer(props.scrollElem)

        // Use a low threshold for maximum compatibility.
        // Safari/Mac can fail with higher thresholds on tall elements
        // inside scroll containers.
        let threshold = 0.01
        if (screen.isMobile) {
            threshold = 0
        }

        const obsOptions: IntersectionObserverInit = {
            threshold,
            // Use the scroll container as root so the observer detects
            // elements scrolling into the container's visible area,
            // not just the browser viewport. This is the key fix for
            // Safari/Mac where the default root (viewport) misses
            // elements inside overflow:auto containers.
            root: scrollContainer || null,
            rootMargin: '100px 0px 100px 0px',
            ...props.options,
        }

        let observer: IntersectionObserver
        try {
            observer = new IntersectionObserver(inViewport, obsOptions)
        } catch (e) {
            // Fallback: if the root element is rejected (e.g. not a valid
            // ancestor), retry with null root
            observer = new IntersectionObserver(inViewport, {
                ...obsOptions,
                root: null,
            })
        }
        observerRef.current = observer

        const attr = props.attr ? props.attr : 'data-inviewport'

        // Schedule a fallback visibility check for an element.
        // If the IntersectionObserver doesn't fire within the timeout,
        // we manually check if the element is visible and force it.
        function scheduleFallback(el: Element) {
            if (fallbackTimers.current.has(el)) {
                return
            }
            const timer = setTimeout(() => {
                fallbackTimers.current.delete(el)
                if (!el.classList.contains('visible')) {
                    if (isElementVisible(el, scrollContainer)) {
                        makeVisible(el)
                    }
                }
            }, 2000)
            fallbackTimers.current.set(el, timer)
        }

        // Observe all currently existing elements
        function observeElements() {
            const elements = document.querySelectorAll(`[${attr}]`)
            elements.forEach((el) => {
                // Skip elements that are already visible
                if (el.classList.contains('visible')) {
                    return
                }
                if (!observedElements.current.has(el)) {
                    observedElements.current.add(el)
                    observer.observe(el)
                    // Safety net: schedule a fallback check in case the
                    // observer never fires for this element
                    scheduleFallback(el)
                }
            })
        }

        // Initial observe after a short delay to allow sibling components to render
        const initialTimeout = setTimeout(observeElements, 100)

        // Use MutationObserver to catch elements added to the DOM after initial render
        const mutationObserver = new MutationObserver(() => {
            observeElements()
        })
        mutationObserver.observe(document.body, { childList: true, subtree: true })

        // Periodic scroll-based fallback check.
        // On scroll, verify any still-hidden elements and make them visible
        // if they're in the visible area. This catches edge cases where both
        // IntersectionObserver and the timed fallback miss an element.
        function handleScrollFallback() {
            observedElements.current.forEach((el) => {
                if (!el.classList.contains('visible') && isElementVisible(el, scrollContainer)) {
                    makeVisible(el)
                }
            })
        }
        const scrollTarget = scrollContainer || window
        scrollTarget.addEventListener('scroll', handleScrollFallback, { passive: true })

        return () => {
            clearTimeout(initialTimeout)
            mutationObserver.disconnect()
            scrollTarget.removeEventListener('scroll', handleScrollFallback)
            // Clear all fallback timers
            fallbackTimers.current.forEach((timer) => clearTimeout(timer))
            fallbackTimers.current.clear()
            observedElements.current.forEach((el) => {
                observer.unobserve(el)
            })
            observedElements.current.clear()
            observer.disconnect()
            observerRef.current = null
        }
    }, [screen.isMobile, screen.orientation, inViewport, makeVisible, props.options, props.attr, props.scrollElem])

    return null
}
